
import { format } from "date-fns";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { parseStoredDateTime } from "@/utils/timeUtils";
import { ArrowUpDown, FileText, User, Search } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";

interface Booking {
  id: number;
  start_time: string;
  end_time: string;
  customer_name: string;
  customer_email?: string;
  customer_id?: string;
  status: string;
  note?: string;
  // New columns for simplified local time approach
  booking_date?: string;
  start_time_local?: string;
  end_time_local?: string;
}
interface BookingListProps {
  date: Date;
  bookings: Booking[];
  onModifyBlock: (booking: Booking) => void;
  onDeleteBlock: (booking: Booking) => void;
  onViewBooking: (booking: Booking) => void;
  onAcceptBooking: (bookingId: number) => void;
  onDeclineBooking: (bookingId: number) => void;
}
type SortField = 'date' | 'customer' | 'status';
type SortDirection = 'asc' | 'desc';

export const BookingList = ({
  date,
  bookings,
  onModifyBlock,
  onDeleteBlock,
  onViewBooking,
  onAcceptBooking,
  onDeclineBooking
}: BookingListProps) => {
  const navigate = useNavigate();
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [searchTerm, setSearchTerm] = useState('');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleRowClick = (booking: Booking) => {
    if (booking.status === 'blocked') {
      onModifyBlock(booking);
    } else if (booking.status === 'travel') {
      onDeleteBlock(booking);
    } else {
      // Navigate to booking details page
      navigate(`/booking/${booking.id}/details`);
    }
  };

  // Filter bookings by search term
  const filteredBookings = bookings.filter(booking =>
    booking.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    booking.customer_email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedBookings = [...filteredBookings].sort((a, b) => {
    const multiplier = sortDirection === 'asc' ? 1 : -1;
    // Use new columns if available, fall back to old ones
    const dateA = (a as any).booking_date && (a as any).start_time_local 
      ? parseStoredDateTime((a as any).booking_date, (a as any).start_time_local)
      : parseStoredDateTime(a.start_time);
    const dateB = (b as any).booking_date && (b as any).start_time_local
      ? parseStoredDateTime((b as any).booking_date, (b as any).start_time_local)  
      : parseStoredDateTime(b.start_time);
    const dateDiff = dateA.getTime() - dateB.getTime();
    if (sortField === 'date') {
      return multiplier * dateDiff;
    }
    if (dateDiff === 0) {
      const timeA = dateA.getHours() * 60 + dateA.getMinutes();
      const timeB = dateB.getHours() * 60 + dateB.getMinutes();
      return timeA - timeB;
    }
    switch (sortField) {
      case 'customer':
        return multiplier * a.customer_name.localeCompare(b.customer_name);
      case 'status':
        return multiplier * a.status.localeCompare(b.status);
      default:
        return 0;
    }
  });

  const SortButton = ({
    field,
    children
  }: {
    field: SortField;
    children: React.ReactNode;
  }) => <Button variant="ghost" onClick={() => handleSort(field)} className="h-8 px-2 hover:bg-transparent">
      {children}
      <ArrowUpDown className="ml-2 h-4 w-4" />
    </Button>;

  return <div className="bg-white p-3 md:p-6 rounded-lg shadow-sm">
      {/* Search Input */}
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            type="text"
            placeholder="Search by customer name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Mobile Sort Controls */}
      <div className="block md:hidden mb-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Sort by:</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleSort('date')}
            className="flex items-center gap-1"
          >
            Date/Time
            <ArrowUpDown className={`h-3 w-3 ${sortField === 'date' ? 'text-primary' : 'text-gray-400'}`} />
          </Button>
        </div>
      </div>

      {/* Mobile Card Layout */}
      <div className="block md:hidden space-y-3">
        {sortedBookings.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {searchTerm ? 'No bookings match your search' : 'No bookings found'}
          </div>
        ) : (
          sortedBookings.map(booking => (
            <div
              key={booking.id}
              onClick={() => handleRowClick(booking)}
              className="border rounded-lg p-4 cursor-pointer transition-colors hover:bg-gray-50"
            >
              <div className="flex justify-between items-start mb-2">
                <div className="font-medium">
                  {(booking as any).booking_date && (booking as any).start_time_local 
                    ? format(parseStoredDateTime((booking as any).booking_date, (booking as any).start_time_local), 'MMM d, yyyy')
                    : format(parseStoredDateTime(booking.start_time), 'MMM d, yyyy')}
                </div>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium
                  ${booking.status === 'new' ? 'bg-yellow-100 text-yellow-800' : 
                    booking.status === 'accepted' ? 'bg-green-100 text-green-800' : 
                    booking.status === 'declined' ? 'bg-red-100 text-red-800' : 
                    booking.status === 'travel' ? 'bg-gray-100 text-gray-600' : 
                    booking.status === 'removed by admin' ? 'bg-gray-100 text-gray-700' :
                    'bg-gray-100 text-gray-800'}`}>
                  {booking.status}
                </span>
              </div>
              
              <div className="text-sm text-gray-600 mb-2">
                {(booking as any).booking_date && (booking as any).start_time_local && (booking as any).end_time_local
                  ? `${format(parseStoredDateTime((booking as any).booking_date, (booking as any).start_time_local), 'HH:mm')} - ${format(parseStoredDateTime((booking as any).booking_date, (booking as any).end_time_local), 'HH:mm')}`
                  : `${format(parseStoredDateTime(booking.start_time), 'HH:mm')} - ${format(parseStoredDateTime(booking.end_time), 'HH:mm')}`}
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {booking.status === 'blocked' ? (
                    <span className="text-gray-500">Blocked</span>
                  ) : booking.status === 'travel' ? (
                    <span className="text-gray-500">Travel Block</span>
                  ) : (
                    <div className="flex items-center gap-1">
                      <User className="h-4 w-4 text-gray-400" />
                      <span className="font-medium">{booking.customer_name}</span>
                      {booking.note && <FileText className="h-4 w-4 text-gray-400" aria-label="Has note" />}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop Table Layout */}
      <div className="hidden md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="py-2">
                <SortButton field="date">Date</SortButton>
              </TableHead>
              <TableHead className="py-2">Time</TableHead>
              <TableHead className="py-2">
                <SortButton field="customer">Customer</SortButton>
              </TableHead>
              <TableHead className="py-2">
                <SortButton field="status">Status</SortButton>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedBookings.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-gray-500">
                  {searchTerm ? 'No bookings match your search' : 'No bookings found'}
                </TableCell>
              </TableRow>
            ) : (
              sortedBookings.map(booking => (
                <TableRow 
                  key={booking.id} 
                  onClick={() => handleRowClick(booking)} 
                  className="cursor-pointer transition-colors hover:bg-gray-50"
                >
                  <TableCell className="py-3">
                    {(booking as any).booking_date && (booking as any).start_time_local 
                      ? format(parseStoredDateTime((booking as any).booking_date, (booking as any).start_time_local), 'MMM d, yyyy')
                      : format(parseStoredDateTime(booking.start_time), 'MMM d, yyyy')}
                  </TableCell>
                  <TableCell className="py-3">
                    {(booking as any).booking_date && (booking as any).start_time_local && (booking as any).end_time_local
                      ? `${format(parseStoredDateTime((booking as any).booking_date, (booking as any).start_time_local), 'HH:mm')} - ${format(parseStoredDateTime((booking as any).booking_date, (booking as any).end_time_local), 'HH:mm')}`
                      : `${format(parseStoredDateTime(booking.start_time), 'HH:mm')} - ${format(parseStoredDateTime(booking.end_time), 'HH:mm')}`}
                  </TableCell>
                  <TableCell className="py-3">
                    {booking.status === 'blocked' ? (
                      <span className="text-gray-500">Blocked</span>
                    ) : booking.status === 'travel' ? (
                      <span className="text-gray-500">Travel Block</span>
                    ) : (
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-400" />
                        <span>{booking.customer_name}</span>
                        {booking.note && <FileText className="h-4 w-4 text-gray-400" aria-label="Has note" />}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="py-3">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium
                      ${booking.status === 'new' ? 'bg-yellow-100 text-yellow-800' : 
                        booking.status === 'accepted' ? 'bg-green-100 text-green-800' : 
                        booking.status === 'declined' ? 'bg-red-100 text-red-800' : 
                        booking.status === 'travel' ? 'bg-gray-100 text-gray-600' : 
                        booking.status === 'removed by admin' ? 'bg-gray-100 text-gray-700' :
                        'bg-gray-100 text-gray-800'}`}>
                      {booking.status}
                    </span>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>;
};
